import type { PhenotypeModel } from '@/config/uiConfig'
import {
  categoryChipClass,
  categorySectionDotClass,
  type ClinicalSummaryCategory,
} from './clinicalSummaryCategoryStyles'
import type { ClinicalSummaryChipOrder } from './clinicalSummaryChipOrder'

export interface ClinicalSummaryPhenotypeSection {
  key: string
  label: string
  /** Stored categories listed in this section, in the order the terms arrive. */
  categories: readonly ClinicalSummaryCategory[]
  dotClass: string
}

export interface ClinicalSummaryPhenotypeOption {
  /** What choosing it stores. */
  category: ClinicalSummaryCategory
  label: string
  /** Stored categories it reads as selected for. */
  covers: readonly ClinicalSummaryCategory[]
  /** The segment's drawn width; a longer label still fits. */
  widthClass: string
}

export interface ClinicalSummaryPhenotypeAddAction {
  /** What adding through it stores. */
  category: ClinicalSummaryCategory
  label: string
}

export interface ClinicalSummaryPhenotypeModelSpec {
  sections: readonly ClinicalSummaryPhenotypeSection[]
  /** The needs-input row control and the chip's category control. */
  options: readonly ClinicalSummaryPhenotypeOption[]
  /** A search result's buttons in the Add phenotype popover; the first is also Enter's. */
  addActions: readonly ClinicalSummaryPhenotypeAddAction[]
  /** Category names in decision and add toasts ("<label> added as <name>"). */
  categoryNames: Record<ClinicalSummaryCategory, string>
  tooltipLabels: Record<ClinicalSummaryCategory, string>
  chipOrder: ClinicalSummaryChipOrder
  /** A chip opens a menu holding Move, Show in summary and Remove (S03); else an inline control and ×. */
  chipMenu: boolean
  /** Hovering a chip rings the phrase its tooltip quotes (S02); Rady's chips ring nothing (R08). */
  chipHoverRingsQuote: boolean
  /** A decided needs-input row collapses over the settle beat before it leaves (R09, S04). */
  settlesDecidedRow: boolean
  chipClass: (category: ClinicalSummaryCategory) => string
  /** Differential diagnoses and prior genetic testing are asked for (else only shown when saved). */
  collectsDifferential: boolean
  /** Where the note about terms no longer in the summary sits: under the field (R11) or above the chips (S05). */
  orphanNote: 'under-field' | 'above-chips'
  toasts: {
    /** A re-extraction that leaves terms out of the summary says so in a toast that owns the Undo. */
    trim: boolean
    /** Opening an order with saved decisions summarises them in a toast. */
    reopen: boolean
  }
}

/**
 * Rady follows R06/R08/R09: Key · Additional · Pertinent Negative. SeqOne follows S01/S02/S04:
 * Present (stored as Key; an existing Additional term shows there too) · Pertinent negatives, with
 * every present chip in the Additional style.
 */
export const CLINICAL_SUMMARY_PHENOTYPE_MODELS: Record<PhenotypeModel, ClinicalSummaryPhenotypeModelSpec> = {
  rady: {
    sections: [
      { key: 'Key', label: 'Key', categories: ['Key'], dotClass: categorySectionDotClass('Key') },
      {
        key: 'Additional',
        label: 'Additional',
        categories: ['Additional'],
        dotClass: categorySectionDotClass('Additional'),
      },
      {
        key: 'Excluded',
        label: 'Pertinent Negative',
        categories: ['Excluded'],
        dotClass: categorySectionDotClass('Excluded'),
      },
    ],
    options: [
      { category: 'Key', label: 'Key', covers: ['Key'], widthClass: 'min-w-[46px]' },
      { category: 'Additional', label: 'Additional', covers: ['Additional'], widthClass: 'min-w-[86px]' },
      { category: 'Excluded', label: 'Pertinent Negative', covers: ['Excluded'], widthClass: 'min-w-[132px]' },
    ],
    addActions: [{ category: 'Key', label: 'Add' }],
    categoryNames: { Key: 'Key', Additional: 'Additional', Excluded: 'Pertinent Negative' },
    tooltipLabels: { Key: 'Key phenotype', Additional: 'Additional phenotype', Excluded: 'Pertinent Negative' },
    chipOrder: 'document',
    chipMenu: false,
    chipHoverRingsQuote: false,
    settlesDecidedRow: true,
    chipClass: categoryChipClass,
    collectsDifferential: true,
    orphanNote: 'under-field',
    toasts: { trim: true, reopen: true },
  },
  seqone: {
    sections: [
      {
        key: 'Present',
        label: 'Present',
        categories: ['Key', 'Additional'],
        dotClass: categorySectionDotClass('Additional'),
      },
      {
        key: 'Excluded',
        label: 'Pertinent negatives',
        categories: ['Excluded'],
        dotClass: categorySectionDotClass('Excluded'),
      },
    ],
    options: [
      { category: 'Key', label: 'Present', covers: ['Key', 'Additional'], widthClass: 'min-w-[76px]' },
      { category: 'Excluded', label: 'Pertinent negative', covers: ['Excluded'], widthClass: 'min-w-[132px]' },
    ],
    addActions: [
      { category: 'Key', label: 'Present' },
      { category: 'Excluded', label: 'Absent' },
    ],
    categoryNames: { Key: 'Present', Additional: 'Present', Excluded: 'Pertinent negative' },
    tooltipLabels: { Key: 'Present', Additional: 'Present', Excluded: 'Pertinent negative' },
    chipOrder: 'document-then-placed',
    chipMenu: true,
    chipHoverRingsQuote: true,
    settlesDecidedRow: true,
    chipClass: (category) => categoryChipClass(category === 'Excluded' ? 'Excluded' : 'Additional'),
    collectsDifferential: false,
    orphanNote: 'above-chips',
    // No S frame draws a status row, and S07 opens without a toast.
    toasts: { trim: false, reopen: false },
  },
}
